import { useCallback, useEffect, useState } from "react";
import type { AutocompleteOption } from "@/components/Autocomplete";
import { Autocomplete } from "@/components/Autocomplete";
import { Button } from "@/components/ui/Button";
import { formatDateTime } from "@/lib/format";
import { normalizeEmployeeLogin } from "@/lib/auth";
import type { Order } from "@/lib/projects";
import {
  grantLateHourPermission,
  listLateHourPermissions,
  revokeLateHourPermission,
  searchEmployees,
  type LateHourPermission,
} from "@/lib/projectOverview";
import { OverviewCard } from "@/projectOverview/OverviewCard";

export function LateHourPermissionsSection({
  project,
}: {
  project: Order;
}) {
  const [rows, setRows] = useState<LateHourPermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState<{ login: string; display: string } | null>(null);
  const [pickerKey, setPickerKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listLateHourPermissions(project.id);
      setRows(Array.isArray(list) ? list : []);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Berechtigungen konnten nicht geladen werden.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleGrant() {
    if (!selected) return;
    setBusy(true);
    setStatus("");
    try {
      await grantLateHourPermission(project.id, {
        user_login: selected.login,
        user_display: selected.display,
      });
      setSelected(null);
      setPickerKey((key) => key + 1);
      setStatus(`${selected.display} darf wieder Zeiten schreiben.`);
      await load();
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Berechtigung konnte nicht vergeben werden.");
    } finally {
      setBusy(false);
    }
  }

  async function handleRevoke(row: LateHourPermission) {
    const label = row.user_display || row.user_login;
    if (!window.confirm(`Berechtigung für ${label} wirklich entziehen?`)) return;
    setBusy(true);
    setStatus("");
    try {
      await revokeLateHourPermission(project.id, row.user_login);
      setStatus(`Berechtigung für ${label} entzogen.`);
      await load();
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Berechtigung konnte nicht entzogen werden.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <OverviewCard title="Nachträgliche Stunden">
      <p className="text-sm text-label-secondary">
        Diese Mitarbeiter dürfen auf diesem abgeschlossenen oder bezahlten Projekt
        weiterhin Zeiten schreiben.
      </p>

      {loading ? (
        <p className="mt-3 text-sm text-label-tertiary">Lade Berechtigungen …</p>
      ) : rows.length === 0 ? (
        <p className="mt-3 text-sm text-label-tertiary">Noch niemand freigeschaltet.</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {rows.map((row) => (
            <li
              key={`${row.user_login}-${row.id}`}
              className="flex items-start justify-between gap-3 rounded-ios border border-separator bg-fill-tertiary/40 px-3 py-2"
            >
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-label">
                  {row.user_display || row.user_login}
                </div>
                <div className="truncate text-xs text-label-tertiary">
                  {row.user_login}
                  {row.granted_at ? ` · seit ${formatDateTime(row.granted_at)}` : ""}
                  {row.granted_by_display ? ` · von ${row.granted_by_display}` : ""}
                </div>
              </div>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={busy}
                onClick={() => void handleRevoke(row)}
              >
                Entziehen
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 space-y-2">
        <Autocomplete<{ login: string; display: string }>
          key={pickerKey}
          label="Mitarbeiter freischalten"
          placeholder="Name suchen…"
          minChars={0}
          loadOptions={async (q) => {
            const employees = await searchEmployees(q, 20);
            const granted = new Set(rows.map((row) => normalizeEmployeeLogin(row.user_login)));
            return employees
              .filter((row) => !granted.has(normalizeEmployeeLogin(row.id)))
              .map(
                (row): AutocompleteOption<{ login: string; display: string }> => ({
                  key: row.id,
                  label: row.label || row.name || row.id,
                  description: row.subtitle,
                  value: {
                    login: normalizeEmployeeLogin(row.id),
                    display: row.label || row.name || row.id,
                  },
                }),
              );
          }}
          onSelect={(opt) => setSelected(opt ? opt.value : null)}
        />
        <Button type="button" size="sm" disabled={busy || !selected} onClick={() => void handleGrant()}>
          Freischalten
        </Button>
      </div>
      {status ? <p className="mt-2 text-xs text-label-tertiary">{status}</p> : null}
    </OverviewCard>
  );
}
